import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GENRES } from "@/lib/categories";
import { useFavorites } from "@/hooks/useFavorites";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "热门榜单", to: "/category/全部?sort=hot" },
  { label: "我的收藏", to: "/favorites" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useFavorites();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname, location.search]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    return location.pathname.startsWith(to.split("?")[0]) && to !== "/category/全部?sort=hot";
  };

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const keyword = query.trim();
    if (!keyword) return;
    navigate(`/search?q=${encodeURIComponent(keyword)}`);
    setQuery("");
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b backdrop-blur-xl transition-colors duration-300 ${
        scrolled
          ? "border-background-200/70 bg-background-50/95"
          : "border-background-200/30 bg-background-50/70"
      }`}
    >
      <div className="flex h-16 w-full items-center gap-3 px-4 md:h-20 md:gap-6 md:px-8 lg:px-10">
        <Link to="/" className="flex shrink-0 items-center gap-2.5" aria-label="免费看大片首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 md:h-10 md:w-10">
            <i className="ri-play-fill text-xl" />
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base font-semibold tracking-tight text-foreground-50 md:text-lg">
              免费看大片
            </span>
            <span className="mt-0.5 hidden font-heading text-[10px] uppercase tracking-[0.22em] text-foreground-500 sm:block">
              Free Blockbuster
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-0.5 lg:flex">
          <Link
            to="/"
            className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
              isActive("/")
                ? "text-primary-300"
                : "text-foreground-300 hover:text-foreground-50"
            }`}
          >
            首页
          </Link>

          <div
            className="relative"
            onMouseLeave={() => setCategoryOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCategoryOpen((prev) => !prev)}
              onMouseEnter={() => setCategoryOpen(true)}
              className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                location.pathname.startsWith("/category/")
                  ? "text-primary-300"
                  : "text-foreground-300 hover:text-foreground-50"
              }`}
            >
              电影分类
              <i className="ri-arrow-down-s-line text-base" />
            </button>
            <div
              className={`absolute left-0 top-full w-[560px] pt-2 transition-all duration-200 ${
                categoryOpen
                  ? "visible translate-y-0 opacity-100"
                  : "invisible -translate-y-1 opacity-0"
              }`}
            >
              <div className="rounded-lg border border-background-200/70 bg-background-100 p-3">
                <div className="mb-2 flex items-center justify-between px-1">
                  <span className="text-xs text-foreground-500">按类型浏览</span>
                  <Link
                    to="/category/全部"
                    className="text-xs text-primary-300 hover:text-primary-200"
                  >
                    查看全部影片
                  </Link>
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {GENRES.map((genre) => (
                    <Link
                      key={genre.name}
                      to={`/category/${encodeURIComponent(genre.name)}`}
                      className="flex items-center gap-2 rounded-md px-2.5 py-2 text-sm text-foreground-300 transition-colors hover:bg-background-200/70 hover:text-foreground-50"
                    >
                      <span className="flex h-6 w-6 items-center justify-center rounded-md bg-background-200/80 text-primary-300">
                        <i className={`${genre.icon} text-sm`} />
                      </span>
                      {genre.name}片
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {NAV_ITEMS.slice(1).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                isActive(item.to)
                  ? "text-primary-300"
                  : "text-foreground-300 hover:text-foreground-50"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <form
            onSubmit={submitSearch}
            role="search"
            className="hidden items-center gap-1.5 rounded-full border border-background-200 bg-background-100 px-3 py-1.5 transition-colors focus-within:border-primary-500/70 md:flex"
          >
            <i className="ri-search-line text-sm text-foreground-500" />
            <input
              type="search"
              name="header-keyword"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索影片、导演、演员"
              aria-label="搜索影片"
              className="w-36 bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-600 lg:w-48"
            />
            <button
              type="submit"
              aria-label="提交搜索"
              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full text-foreground-400 transition-colors hover:text-primary-300"
            >
              <i className="ri-arrow-right-line text-sm" />
            </button>
          </form>

          <Link
            to="/favorites"
            aria-label={`我的收藏，共${count}部`}
            className="relative flex h-9 w-9 items-center justify-center rounded-md border border-background-200 bg-background-100 text-foreground-300 transition-colors hover:border-accent-500/50 hover:text-accent-300"
          >
            <i className="ri-heart-3-line text-base" />
            {count > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 font-heading text-[10px] font-semibold text-background-50">
                {count > 99 ? "99+" : count}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobileOpen}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-200 bg-background-100 text-foreground-200 transition-colors hover:text-primary-300 lg:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-4-line"} text-lg`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[calc(100vh-4rem)] overflow-y-auto border-t border-background-200/70 bg-background-50 px-4 pb-6 pt-4 lg:hidden">
          <form
            onSubmit={submitSearch}
            role="search"
            className="flex w-full items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2.5 focus-within:border-primary-500/70"
          >
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              type="search"
              name="mobile-keyword"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索影片、导演、演员"
              aria-label="搜索影片"
              className="w-full bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-600"
            />
            <button
              type="submit"
              aria-label="提交搜索"
              className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50"
            >
              <i className="ri-search-line text-sm" />
            </button>
          </form>

          <div className="mt-4 flex flex-col">
            <Link
              to="/"
              className="flex items-center justify-between rounded-md px-3 py-3 text-sm text-foreground-100 transition-colors hover:bg-background-100"
            >
              首页
              <i className="ri-arrow-right-s-line text-foreground-500" />
            </Link>
            {NAV_ITEMS.slice(1).map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="flex items-center justify-between rounded-md px-3 py-3 text-sm text-foreground-100 transition-colors hover:bg-background-100"
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-500" />
              </Link>
            ))}
          </div>

          <div className="mt-4 border-t border-background-200/70 pt-4">
            <p className="px-3 text-xs text-foreground-500">电影分类</p>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {GENRES.map((genre) => (
                <Link
                  key={genre.name}
                  to={`/category/${encodeURIComponent(genre.name)}`}
                  className="flex items-center gap-1.5 rounded-md bg-background-100 px-2.5 py-2 text-xs text-foreground-200"
                >
                  <i className={`${genre.icon} text-primary-300`} />
                  {genre.name}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}